import { GridCellParams } from '@mui/x-data-grid';
import { t } from 'i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep, debounce } from 'lodash';
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useCepListConfig from './CepList.config';
import { PAGINATION_PAGE_SIZE } from 'basics/constants/common.constant';
import {
  mayaCepCreateRoute,
  mayaCepDuplicateRoute,
  mayaCepEditRoute,
  mayaCepTemplateSelectorRoute,
  mayaCepViewRoute,
} from 'basics/constants/routes.constants';
import { AllValidTherapeuticArea } from 'basics/enums/biogen.enums';
import { CepStatus, CepStatusActions, VeevaSyncStatus, BiogenFunction } from 'basics/enums/maya.enums';
import { useSetCepStatusToApprovedMutation } from 'basics/graphql/mutations/setCepStatusToApproved';
import { useSetCepStatusToCancelledMutation } from 'basics/graphql/mutations/setCepStatusToCancelled';
import { useSetCepStatusToDeletedMutation } from 'basics/graphql/mutations/setCepStatusToDeleted';
import { useSetCepStatusToFeedbackMutation } from 'basics/graphql/mutations/setCepStatusToFeedback';
import { useSetCepStatusToRestoredMutation } from 'basics/graphql/mutations/setCepStatusToRestored';
import { useSyncCepToVeevaMutation } from 'basics/graphql/mutations/syncCepToVeeva';
import { useSyncTacticGroupsToVeevaMutation } from 'basics/graphql/mutations/syncTacticGroupsToVeeva';
import useGetAllCepQuery from 'basics/graphql/queries/getAllCeps';
import useGetCepByIdLazyQuery from 'basics/graphql/queries/getCepByIdLazy';
import { SelectOption } from 'basics/options/options.types';
import { ProductsOptionType } from 'basics/types/products.types';
import { succesToast, warningToast } from 'basics/utils/toast';
import { useTacticFieldsValidation } from 'components/TacticFieldsValidationDialog';
import useTacticValidation from 'components/TacticValidationDialog/TacticValidation.hook';
import { useCurrentUserState } from 'contexts/user';
import { CustomerEngagementPlanning, SortFilter } from 'generated/maya.types';
import { useYodaCreateForm } from 'yoda-ui/yodaForm';

const useCepList = () => {
  const { user } = useCurrentUserState();
  const { providerFields, setValue } = useYodaCreateForm();
  const [cepSortFilter, setCepSortFilter] = useState(SortFilter.NEWEST);
  const [cepSearchFilter, setCepSearchFilter] = useState<(string)>();
  const [customerInitiativeFilter, setCustomerInitiativeFilter] = useState<(string | number)[]>([]);
  const [statusFilter, setStatusFilter] = useState<(string | number)[]>([]);
  const [cepTherapeuticAreaFilter, setCepTherapeuticAreaFilter] = useState<string[]>([]);
  const [cepProductFilter, setCepProductFilter] = useState<string[]>([]);
  const [cepBiogenFunctionFilter, setCepBiogenFunctionFilter] = useState<BiogenFunction[]>([]);
  const [ceps, setCeps] = useState<CustomerEngagementPlanning[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<{
    status: SelectOption[];
    customerInitiative: SelectOption[];
    therapeuticArea: SelectOption[];
    product: SelectOption[];
    cepFunction: SelectOption[];
  }>({
    status: [],
    customerInitiative: [],
    therapeuticArea: [],
    product: [],
    cepFunction: [],
  });
  const hasActiveFilters = Object.values(selectedFilters).some((options) => options.length > 0);

  const flags = useFlags();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGINATION_PAGE_SIZE);
  const [rowCountState, setRowCountState] = useState(0);

  const [open, setOpen] = useState(false);
  const [openTacticValidationDialog, setOpenTacticValidationDialog] = useState(false);
  const [confirmationDialogConfig, setConfirmationDialogConfig] = useState({
    dialogTitle: '',
    onConfirm: () => {},
  });

  const [selectedCepId, setSelectedCepId] = useState<string>();
  const navigate = useNavigate();

  const queryOptions = useMemo(
    () => ({
      limit: pageSize,
      offset: page * pageSize,
    }),
    [page, pageSize],
  );

  const queryFilters = useMemo(
    () => ({
      search: cepSearchFilter,
      status: statusFilter.length ? statusFilter : [CepStatus.DRAFT, CepStatus.APPROVED, CepStatus.FEEDBACK, CepStatus.CANCELLED],
      customerInitiative: customerInitiativeFilter.length ? customerInitiativeFilter : undefined,
      therapeuticArea: cepTherapeuticAreaFilter.length ? cepTherapeuticAreaFilter : null,
      product: cepProductFilter.length ? cepProductFilter : null,
      cepFunction: cepBiogenFunctionFilter.length ? cepBiogenFunctionFilter : null,
      isTemplate: false,
      countries: user?.country ? [user?.country] : undefined,
    }),
    [
      cepSearchFilter,
      customerInitiativeFilter,
      statusFilter,
      cepTherapeuticAreaFilter,
      cepProductFilter,
      cepBiogenFunctionFilter,
      user?.country,
    ],
  );

  const { data: cepData, loading: cepLoading, count } = useGetAllCepQuery(
    queryFilters,
    queryOptions,
    true,
    cepSortFilter,
  );
  const [setCepStatusToFeedback] = useSetCepStatusToFeedbackMutation();
  const [setCepStatusToApproved] = useSetCepStatusToApprovedMutation();
  const [setCepStatusToDeleted, { loading: removeCepLoading }] = useSetCepStatusToDeletedMutation();
  const [setCepStatusToCancelled, { loading: cancelCepLoading }] = useSetCepStatusToCancelledMutation();
  const [setCepStatusToRestored, { loading: restoreCepLoading }] = useSetCepStatusToRestoredMutation();
  const [syncCepToVeeva] = useSyncCepToVeevaMutation();
  const [syncTacticGroupsToVeeva] = useSyncTacticGroupsToVeevaMutation();
  const { queryGetCepById } = useGetCepByIdLazyQuery();
  const { validateGroupRelationsLazy } = useTacticValidation();
  const {
    invalidTactics,
    cepDateError,
    validationError,
    validateTacticsFields,
  } = useTacticFieldsValidation();
  const [openFieldsValidationDialog, setOpenFieldsValidationDialog] = useState(false);
  const [generalLoading, setGeneralLoading] = useState(false);

  useEffect(() => {
    setRowCountState((prevRowCountState) => (count !== undefined ? count : prevRowCountState));
  }, [count, setRowCountState]);

  useEffect(() => {
    if (!cepLoading) {
      setCeps(cepData);
    }
  }, [cepData, cepLoading]);

  const refreshCepStatus = (newStatus: CepStatus, cepId: string) => {
    const clonedCeps = cloneDeep(ceps);
    const targetCepIndex = ceps.findIndex((cep) => { return cep._id === cepId; });
    if (targetCepIndex >= 0) {
      const updatedTargetCep = cloneDeep(ceps[targetCepIndex]);
      updatedTargetCep.status = newStatus;
      clonedCeps.splice(targetCepIndex, 1, updatedTargetCep);
      setCeps(clonedCeps);
    }
  };

  const getTacticsValidationOptions = async (
    cepId: string,
  ): Promise<{
    validateRequiredFields: boolean;
    validateOptionsAvailability: boolean;
  }> => {
    const { data: currentCepResponse } = await queryGetCepById({
      variables: { id: cepId },
    });

    const currentCep = currentCepResponse?.getCustomerEngagementPlanningById;
    const currentCepFromList = ceps.find((cep) => cep._id === cepId);
    const sourceCepId = currentCep?.sourceId || currentCepFromList?.sourceId;

    if (!currentCep) {
      return {
        validateRequiredFields: false,
        validateOptionsAvailability: false,
      };
    }

    const currentCustomerInitiative = currentCep.customerInitiative || currentCepFromList?.customerInitiative;

    if (currentCustomerInitiative?.isTemplate) {
      return {
        validateRequiredFields: true,
        validateOptionsAvailability: false,
      };
    }

    if (!sourceCepId) {
      return {
        validateRequiredFields: false,
        validateOptionsAvailability: false,
      };
    }

    const { data: sourceCepResponse } = await queryGetCepById({
      variables: { id: sourceCepId },
    });

    const sourceCep = sourceCepResponse?.getCustomerEngagementPlanningById;

    let templateCustomerInitiative = sourceCep?.customerInitiative?.isTemplate
      ? sourceCep.customerInitiative
      : null;

    if (!templateCustomerInitiative && sourceCep?.sourceId) {
      const { data: parentSourceCepResponse } = await queryGetCepById({
        variables: { id: sourceCep.sourceId },
      });

      const parentSourceCep = parentSourceCepResponse?.getCustomerEngagementPlanningById;

      if (parentSourceCep?.customerInitiative?.isTemplate) {
        templateCustomerInitiative = parentSourceCep.customerInitiative;
      }
    }

    const validateRequiredFields = Boolean(templateCustomerInitiative);
    const validateOptionsAvailability = Boolean(
      validateRequiredFields
        && templateCustomerInitiative?.country
        && currentCustomerInitiative?.country
        && templateCustomerInitiative.country !== currentCustomerInitiative.country,
    );

    return {
      validateRequiredFields,
      validateOptionsAvailability,
    };
  };

  const editCepCallback = async (cepId: string) => {
    navigate(`${mayaCepEditRoute}?id=${cepId}`);
  };

  const duplicateCepCallback = async (cepId: string) => {
    navigate(`${mayaCepDuplicateRoute}?cepSourceId=${cepId}`);
  };

  const setCepToApprovedCallback = async (cepId: string) => {
    setGeneralLoading(true);

    const tacticsValidationOptions = await getTacticsValidationOptions(cepId);

    if (
      tacticsValidationOptions.validateRequiredFields
        || tacticsValidationOptions.validateOptionsAvailability
    ) {
      const tacticFieldsValid = await validateTacticsFields(cepId, tacticsValidationOptions);

      if (!tacticFieldsValid) {
        setOpenFieldsValidationDialog(true);
        setSelectedCepId(cepId);
        setGeneralLoading(false);
        return;
      }
    }

    if (flags.mayaBdcfcsd1585EnableTacticGroup) {
      const tacticsValidationResult = await validateGroupRelationsLazy(cepId);

      if (!tacticsValidationResult && flags.mayaBdcfcsd1585EnableTacticGroup) {
        setOpenTacticValidationDialog(true);
        setSelectedCepId(cepId);
        setGeneralLoading(false);
        return;
      }

      const { data, error } = await syncTacticGroupsToVeeva(cepId);

      if (data && !error) {
        succesToast(t('maya_action_approve_cep_group_success_sync'));
      } else if (error) {
        warningToast(error || t('maya_action_approve_cep_error'));
        setGeneralLoading(false);
        return;
      }
    }

    const { data: cepApproved, error: cepApprovedError } = await setCepStatusToApproved(cepId);
    if (cepApproved && !cepApprovedError) {
      succesToast(t('maya_action_approve_cep_success'));
      refreshCepStatus(CepStatus.APPROVED, cepId);
    } else {
      warningToast(cepApprovedError || t('maya_action_approve_cep_error'));
    }

    setGeneralLoading(false);
  };

  const setCepToFeedbackCallback = async (cepId: string) => {
    setGeneralLoading(true);

    const tacticsValidationOptions = await getTacticsValidationOptions(cepId);

    if (
      tacticsValidationOptions.validateRequiredFields
        || tacticsValidationOptions.validateOptionsAvailability
    ) {
      const tacticFieldsValid = await validateTacticsFields(cepId, tacticsValidationOptions);

      if (!tacticFieldsValid) {
        setOpenFieldsValidationDialog(true);
        setSelectedCepId(cepId);
        setGeneralLoading(false);
        return;
      }
    }

    if (flags.mayaBdcfcsd1585EnableTacticGroup) {
      const tacticsValidationResult = await validateGroupRelationsLazy(cepId);

      if (!tacticsValidationResult && flags.mayaBdcfcsd1585EnableTacticGroup) {
        setOpenTacticValidationDialog(true);
        setSelectedCepId(cepId);
        setGeneralLoading(false);
        return;
      }
    }

    const { data: cepFeedback, error: cepFeedbackError } = await setCepStatusToFeedback(cepId);
    if (cepFeedback && !cepFeedbackError) {
      succesToast(t('maya_action_feedback_cep_success'));
      refreshCepStatus(CepStatus.FEEDBACK, cepId);
    } else {
      warningToast(cepFeedbackError || t('maya_action_feedback_cep_error'));
    }

    setGeneralLoading(false);
  };

  const syncCepCallback = async (cepId: string) => {
    setGeneralLoading(true);

    const tacticsValidationOptions = await getTacticsValidationOptions(cepId);

    if (
      tacticsValidationOptions.validateRequiredFields
        || tacticsValidationOptions.validateOptionsAvailability
    ) {
      const tacticFieldsValid = await validateTacticsFields(cepId, tacticsValidationOptions);

      if (!tacticFieldsValid) {
        setOpenFieldsValidationDialog(true);
        setSelectedCepId(cepId);
        setGeneralLoading(false);
        return;
      }
    }

    const clonedCeps = cloneDeep(ceps);
    const targetCepIndex = clonedCeps.findIndex((cep) => { return cep._id === cepId; });
    const updatedTargetCep = cloneDeep(clonedCeps[targetCepIndex]);

    const { data: cepSynched, error: cepSynchedError } = await syncCepToVeeva(cepId);

    if (cepSynched && !cepSynchedError) {
      succesToast(t('maya_action_sync_cep_success'));
      updatedTargetCep.globalCepSyncStatus = { syncStatus: VeevaSyncStatus.SYNC, errors: [] };
    } else {
      warningToast(cepSynchedError || t('maya_action_sync_cep_error'));
      updatedTargetCep.globalCepSyncStatus = { syncStatus: VeevaSyncStatus.ERROR, errors: [] };
    }

    clonedCeps.splice(targetCepIndex, 1, updatedTargetCep);
    setCeps(clonedCeps);
    setGeneralLoading(false);
  };

  const deleteCepCallback = async (cepId: string) => {
    const { data: cepDeleted, error: cepDeletedError } = await setCepStatusToDeleted(cepId);
    if (cepDeleted && !cepDeletedError) {
      succesToast(t('maya_action_deleting_cep_success'));
      const clonedCeps = cloneDeep(ceps);
      const targetCepIndex = clonedCeps.findIndex((cep) => { return cep._id === cepId; });
      if (targetCepIndex !== -1) {
        clonedCeps.splice(targetCepIndex, 1);
        setCeps(clonedCeps);
      } else {
        warningToast(cepDeletedError || t('errors_standard'));
      }
    }

    setOpen(false);
  };

  const cancelCepCallback = async (cepId: string) => {
    const clonedCeps = cloneDeep(ceps);
    const targetCepIndex = clonedCeps.findIndex((cep) => { return cep._id === cepId; });
    const updatedTargetCep = cloneDeep(clonedCeps[targetCepIndex]);
    const { data: cepCancelled, error: cepCancelledError } = await setCepStatusToCancelled(cepId);

    if (cepCancelled && !cepCancelledError) {
      succesToast(t('maya_action_canceling_cep_success'));
      updatedTargetCep.status = CepStatus.CANCELLED;
      updatedTargetCep.title = `CANCELLED_${updatedTargetCep.title}`;
    } else {
      warningToast(cepCancelledError || t('errors_standard'));
    }

    clonedCeps.splice(targetCepIndex, 1, updatedTargetCep);
    setCeps(clonedCeps);
    setOpen(false);
  };

  const restoreCepCallback = async (cepId: string) => {
    const clonedCeps = cloneDeep(ceps);
    const targetCepIndex = clonedCeps.findIndex((cep) => { return cep._id === cepId; });
    const updatedTargetCep = cloneDeep(clonedCeps[targetCepIndex]);
    const { data: cepRestored, error: cepRestoredError } = await setCepStatusToRestored(cepId, true);

    if (cepRestored && !cepRestoredError) {
      succesToast(t('maya_action_restore_cep_success'));
      updatedTargetCep.status = CepStatus.DRAFT;
      updatedTargetCep.title = updatedTargetCep.title.replace('CANCELLED_', 'RESTORED_');
    } else {
      warningToast(cepRestoredError || t('maya_action_restore_cep_error'));
    }

    clonedCeps.splice(targetCepIndex, 1, updatedTargetCep);
    setCeps(clonedCeps);
    setOpen(false);
  };

  const openModalCallback = (cepId: string, action: CepStatusActions) => {
    setOpen(true);

    if (action === CepStatusActions.DELETE) {
      setConfirmationDialogConfig({
        dialogTitle: t('maya_cep_modal_remove'),
        onConfirm: () => deleteCepCallback(cepId),
      });
    } else if (action === CepStatusActions.CANCEL) {
      setConfirmationDialogConfig({
        dialogTitle: t('maya_cep_modal_cancel'),
        onConfirm: () => cancelCepCallback(cepId),
      });
    } else if (action === CepStatusActions.RESTORE) {
      setConfirmationDialogConfig({
        dialogTitle: t('maya_cep_modal_restore'),
        onConfirm: () => restoreCepCallback(cepId),
      });
    }
  };

  const {
    cepListColumnConfig,
    cepTitleFieldConfig,
    statusFieldConfig,
    customerInitiativeFieldConfig,
    cepTherapeuticAreaFieldConfig,
    cepProductFieldConfig,
    cepBiogenFunctionFieldConfig,
    cepSortFieldConfig,
  } = useCepListConfig(
    setCepToFeedbackCallback,
    setCepToApprovedCallback,
    syncCepCallback,
    editCepCallback,
    duplicateCepCallback,
    openModalCallback,
    cepTherapeuticAreaFilter,
  );

  const newCepClick = () => {
    if (flags.mayaBiogenlinc5098CepTemplateSelector) {
      navigate(mayaCepTemplateSelectorRoute);
    } else {
      navigate(mayaCepCreateRoute);
    }
  };

  const handleChangeCepSearch = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setCepSearchFilter(event.target.value);
  };
  const debouncedHandleChangeCepSearch = useMemo(
    () => debounce(handleChangeCepSearch, 750),
    [],
  );

  const handleChangeCI = useCallback((event: SyntheticEvent<Element, Event>, value: SelectOption[]) => {
    const values = value.map((element) => element.id);
    setCustomerInitiativeFilter(values);
    setSelectedFilters((prev) => ({ ...prev, customerInitiative: value }));
  }, [setCustomerInitiativeFilter]);

  const handleChangeStatus = useCallback((event: SyntheticEvent<Element, Event>, value: SelectOption[]) => {
    const values = value.map((element) => element.id);
    setStatusFilter(values);
    setSelectedFilters((prev) => ({ ...prev, status: value }));
  }, [setStatusFilter]);

  const handleChangeCepTherapeuticArea = useCallback((event: SyntheticEvent<Element, Event>, value: SelectOption[]) => {
    if (value.length === 0) {
      setCepProductFilter([]);
      setValue(cepProductFieldConfig.name, []);
      setSelectedFilters((prev) => ({ ...prev, product: [] }));
    }

    const values = value.map((element) => element.id as string);

    if (values.includes(AllValidTherapeuticArea.BBU)) {
      values.push(AllValidTherapeuticArea.BIOSIMS);
    }

    setCepTherapeuticAreaFilter(values);
    setSelectedFilters((prev) => ({ ...prev, therapeuticArea: value }));
  }, [setValue, setCepTherapeuticAreaFilter, cepProductFieldConfig.name]);

  const handleChangeCepProduct = useCallback((event: SyntheticEvent<Element, Event>, products: ProductsOptionType[]) => {
    const values = products.map((product) => product.id as string);
    setCepProductFilter(values);
    setSelectedFilters((prev) => ({ ...prev, product: products }));
  }, [setCepProductFilter]);

  const handleChangeCepBiogenFunction = useCallback((event: SyntheticEvent<Element, Event>, value: SelectOption[]) => {
    const values = value.map((element) => element.id as BiogenFunction);
    setCepBiogenFunctionFilter(values);
    setSelectedFilters((prev) => ({ ...prev, cepFunction: value }));
  }, [setCepBiogenFunctionFilter]);

  const getFilteredRows = useCallback(() => {
    let filteredRows = ceps;

    if (customerInitiativeFilter.length > 0) {
      filteredRows = cepData.filter((value) => {
        if (typeof value.customerInitiative === 'string') {
          return value.customerInitiative;
        }

        return customerInitiativeFilter.indexOf(value.customerInitiative._id) !== -1;
      });
    }

    if (statusFilter.length > 0) {
      filteredRows = filteredRows.filter((value) => {
        return statusFilter.indexOf(value.status) !== -1;
      });
    }

    return filteredRows;
  }, [ceps, customerInitiativeFilter, statusFilter, cepData]);

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const onPageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const handleClick = (params: GridCellParams) => {
    if (params.field !== 'action') {
      navigate(`${mayaCepViewRoute}?id=${params.row._id}`);
    }
  };

  const menuCustomerEngagementPlanningLabel = t('maya_main_menu_cep_label');

  const newElementButton = {
    newElementLabel: t('maya_list_new_element_label', { elementTitle: menuCustomerEngagementPlanningLabel }),
    newElementClick: newCepClick,
  };

  const handleSortChange = useCallback((value: SelectOption) => {
    setCepSortFilter(value.id as SortFilter);
  }, []);

  const handleRemoveChip = useCallback((
    filterType: 'therapeuticArea' | 'product' | 'status' | 'customerInitiative' | 'cepFunction',
    idToRemove: string | number,
  ) => {
    if (filterType === 'therapeuticArea') {
      const newOptions = selectedFilters.therapeuticArea.filter((o) => o.id !== idToRemove);
      const values = newOptions.map((element) => element.id as string);

      if (values.includes(AllValidTherapeuticArea.BBU)) {
        values.push(AllValidTherapeuticArea.BIOSIMS);
      }

      setCepTherapeuticAreaFilter(values);
      setSelectedFilters((prev) => ({ ...prev, therapeuticArea: newOptions }));
      setValue(cepTherapeuticAreaFieldConfig.name, newOptions);

      if (newOptions.length === 0) {
        setCepProductFilter([]);
        setSelectedFilters((prev) => ({ ...prev, product: [] }));
        setValue(cepProductFieldConfig.name, []);
      }
    } else if (filterType === 'product') {
      const newOptions = selectedFilters.product.filter((o) => o.id !== idToRemove);
      const values = newOptions.map((product) => product.id as string);

      setCepProductFilter(values);
      setSelectedFilters((prev) => ({ ...prev, product: newOptions }));
      setValue(cepProductFieldConfig.name, newOptions);
    } else if (filterType === 'status') {
      const newOptions = selectedFilters.status.filter((o) => o.id !== idToRemove);
      const values = newOptions.map((element) => element.id);
      setStatusFilter(values);
      setSelectedFilters((prev) => ({ ...prev, status: newOptions }));
      setValue(statusFieldConfig.name, newOptions);
    } else if (filterType === 'customerInitiative') {
      const newOptions = selectedFilters.customerInitiative.filter((o) => o.id !== idToRemove);
      const values = newOptions.map((element) => element.id);
      setCustomerInitiativeFilter(values);
      setSelectedFilters((prev) => ({ ...prev, customerInitiative: newOptions }));
      setValue(customerInitiativeFieldConfig.name, newOptions);
    } else if (filterType === 'cepFunction') {
      const newOptions = selectedFilters.cepFunction.filter((o) => o.id !== idToRemove);
      const values = newOptions.map((element) => element.id as BiogenFunction);
      setCepBiogenFunctionFilter(values);
      setSelectedFilters((prev) => ({ ...prev, cepFunction: newOptions }));
      setValue(cepBiogenFunctionFieldConfig.name, newOptions);
    }
  }, [
    selectedFilters,
    setValue,
    cepTherapeuticAreaFieldConfig.name,
    cepProductFieldConfig.name,
    statusFieldConfig.name,
    customerInitiativeFieldConfig.name,
    cepBiogenFunctionFieldConfig.name,
  ]);

  const handleReset = useCallback(() => {
    setCepTherapeuticAreaFilter([]);
    setCepProductFilter([]);
    setStatusFilter([]);
    setCustomerInitiativeFilter([]);
    setCepBiogenFunctionFilter([]);
    setSelectedFilters({
      therapeuticArea: [],
      product: [],
      status: [],
      customerInitiative: [],
      cepFunction: [],
    });
    setValue(cepTherapeuticAreaFieldConfig.name, []);
    setValue(cepProductFieldConfig.name, []);
    setValue(statusFieldConfig.name, []);
    setValue(customerInitiativeFieldConfig.name, []);
    setValue(cepBiogenFunctionFieldConfig.name, []);
  }, [
    setValue,
    cepTherapeuticAreaFieldConfig.name,
    cepProductFieldConfig.name,
    statusFieldConfig.name,
    customerInitiativeFieldConfig.name,
    cepBiogenFunctionFieldConfig.name,
  ]);

  const hasSelectedFilters = selectedFilters.therapeuticArea.length > 0
    || selectedFilters.product.length > 0
    || selectedFilters.status.length > 0
    || selectedFilters.customerInitiative.length > 0
    || selectedFilters.cepFunction.length > 0;

  return {
    cepData,
    cepListColumnConfig,
    cepLoading,
    cepTitleFieldConfig,
    customerInitiativeFieldConfig,
    customerInitiativeFilter,
    debouncedHandleChangeCepSearch,
    cepTherapeuticAreaFieldConfig,
    handleChangeCepTherapeuticArea,
    cepProductFieldConfig,
    handleChangeCepProduct,
    cepBiogenFunctionFieldConfig,
    handleChangeCepBiogenFunction,
    deleteCepCallback,
    cancelCepCallback,
    restoreCepCallback,
    getFilteredRows,
    handleChangeCI,
    handleChangeStatus,
    handleClick,
    menuCustomerEngagementPlanningLabel,
    newElementButton,
    onPageChange,
    onPageSizeChange,
    open,
    removeCepLoading,
    cancelCepLoading,
    restoreCepLoading,
    rowCountState,
    setOpen,
    statusFieldConfig,
    statusFilter,
    confirmationDialogConfig,
    openTacticValidationDialog,
    setOpenTacticValidationDialog,
    openFieldsValidationDialog,
    setOpenFieldsValidationDialog,
    invalidTactics,
    cepDateError,
    validationError,
    selectedCepId,
    generalLoading,
    providerFields,
    selectedFilters,
    handleRemoveChip,
    handleReset,
    hasSelectedFilters,
    cepSortFieldConfig,
    handleSortChange,
    hasActiveFilters,
    cepSortFilter,

    // Returned for Dashboards
    setCepToFeedbackCallback,
    setCepToApprovedCallback,
    syncCepCallback,
    editCepCallback,
    duplicateCepCallback,
    openModalCallback,
  };
};

export default useCepList;
